// frontend/src/components/Templates/applyTemplate.ts
import type { Template } from '../../types';
import type { Part, EstimateLineItem } from '../../types';
import { priceFromCost } from '../../utils/partMarkup';

export function resolveLineRate(
  payload: { labor_rate_id?: number | null; hourly_rate?: number | null },
  rates?: { id: number; hourly_rate: number }[],
): { savedRate: number | null; currentRate: number | null; diff: boolean } {
  const saved = payload.hourly_rate ?? null;
  const id = payload.labor_rate_id ?? null;
  const match = id != null && rates ? rates.find(r => r.id === id) : undefined;
  const current = match ? match.hourly_rate : null;
  const diff = current != null && saved != null && current !== saved;
  return { savedRate: saved, currentRate: current, diff };
}

export function templateToPart(t: Template, lineItemId?: number, presets?: { id: number; markup_percentage: number }[]): Part {
  const p: any = t.payload || {};
  const cost: number | null = p.cost ?? null;
  let markupPresetId: number | null = p.markup_preset_id ?? null;
  let markupPct: number | null = p.markup_percentage ?? null;
  let unit: number = p.unit_price ?? 0;

  if (markupPresetId != null) {
    const preset = presets?.find(x => x.id === markupPresetId);
    if (preset) {
      markupPct = preset.markup_percentage;
      if (cost != null && cost > 0) unit = priceFromCost(cost, markupPct);
    } else {
      markupPresetId = null; // preset deleted → keep snapshot % and price
    }
  }

  const discount = p.discount_amount ?? 0;
  return {
    id: Date.now() + Math.floor(Math.random() * 1000),
    line_item_id: lineItemId,
    part_number: t.part_number || '',
    description: t.description || t.name,
    quantity: 1,
    unit_price: unit,
    total_price: unit,
    discount_percentage: p.discount_percentage ?? 0,
    discount_amount: discount,
    discounted_price: unit - discount,
    availability: p.availability ?? 'in_stock',
    supplier: t.supplier ?? undefined,
    notes: p.notes ?? undefined,
    markup_preset_id: markupPresetId,
    markup_percentage: markupPct,
    cost: cost ?? undefined,
  } as Part;
}

export function templateToLine(t: Template, defaultLaborRate: number, rates?: { id: number; hourly_rate: number }[], presets?: { id: number; markup_percentage: number }[]): EstimateLineItem {
  const p: any = t.payload || {};
  const rr = resolveLineRate(p, rates);
  const hourlyRate = rr.currentRate ?? (p.hourly_rate ?? defaultLaborRate);
  const baseLabor = (p.labor_hours || 0) * hourlyRate;
  const laborDiscount = p.labor_discount_amount || (baseLabor * (p.labor_discount_percentage || 0) / 100);
  const parts: Part[] = (p.parts || []).map((tp: any, i: number) => {
    const tpCost: number | null = tp.cost ?? null;
    let tpPresetId: number | null = tp.markup_preset_id ?? null;
    let tpPct: number | null = tp.markup_percentage ?? null;
    let unit: number = tp.unit_price ?? 0;
    if (tpPresetId != null) {
      const preset = presets?.find(x => x.id === tpPresetId);
      if (preset) {
        tpPct = preset.markup_percentage;
        if (tpCost != null && tpCost > 0) unit = priceFromCost(tpCost, tpPct);
      } else {
        tpPresetId = null;
      }
    }
    const disc = tp.discount_amount ?? 0;
    return {
      id: Date.now() + i + 1,
      part_number: tp.part_number, description: tp.description, quantity: tp.quantity ?? 1,
      unit_price: unit, total_price: unit * (tp.quantity ?? 1),
      discount_percentage: tp.discount_percentage ?? 0, discount_amount: disc,
      discounted_price: unit - disc, availability: tp.availability ?? 'in_stock', supplier: tp.supplier,
      markup_preset_id: tpPresetId,
      markup_percentage: tpPct,
      cost: tpCost ?? undefined,
    } as Part;
  });
  const partsCost = parts.reduce((s, pt) => s + (pt.discounted_price ?? pt.total_price), 0);
  return {
    id: Date.now(),
    category: t.category ?? undefined,
    description: t.description || t.name,
    quantity: p.quantity ?? 1,
    labor_hours: p.labor_hours,
    hourly_rate: hourlyRate,
    labor_rate_id: p.labor_rate_id ?? null,
    labor_discount_percentage: p.labor_discount_percentage,
    labor_discount_amount: laborDiscount,
    total_price: (baseLabor - laborDiscount) + partsCost,
    parts,
    notes: p.notes ?? undefined,
  } as EstimateLineItem;
}
